import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteImages } from "@/context/SiteImagesContext";
import { useSiteContent } from "@/context/SiteContentContext";

const DemoSlider = ({ image }) => {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative w-full rounded-3xl overflow-hidden border border-white/10 select-none aspect-[16/10]" data-testid="upscale-demo-slider">
      {/* 4K (sharp) base */}
      <img src={image} alt="4K" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      {/* 2K (simulated lower quality) overlay clipped */}
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={image} alt="2K" className="absolute inset-0 w-full h-full object-cover" draggable={false}
          style={{ filter: "blur(2px) brightness(0.92) saturate(0.85)" }} />
      </div>
      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs font-semibold">2K</span>
      <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-xs font-semibold">4K · IA</span>
      <div className="absolute top-0 bottom-0 w-0.5 bg-white/90 pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white shadow-lg grid place-items-center text-black text-sm">⇆</div>
      </div>
      <input
        data-testid="upscale-demo-range"
        type="range" min="0" max="100" value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
      />
    </div>
  );
};

export const Upscale4K = () => {
  const { img } = useSiteImages();
  const { t } = useSiteContent();
  return (
    <section id="upscale4k" className="relative py-24 px-6 bg-gradient-to-b from-transparent via-[#0a0a12] to-transparent" data-testid="upscale-section">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-500/15 text-indigo-300 text-xs font-semibold uppercase tracking-wider">
            <Sparkles className="h-3.5 w-3.5" /> {t('upscale_eyebrow')}
          </span>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]">
            {t('upscale_title')} <span className="text-gradient">{t('upscale_highlight')}</span>
          </h2>
          <p className="mt-5 text-white/60 text-lg max-w-md">{t('upscale_subtitle')}</p>
          <Link to="/upscale" data-testid="upscale-section-cta">
            <Button className="mt-8 rounded-full h-13 px-8 py-6 bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 text-white border-0 gap-2 text-base shadow-[0_8px_30px_rgba(99,102,241,0.4)]">
              <Wand2 className="h-5 w-5" /> {t('upscale_cta')} <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
        >
          <DemoSlider image={img('irisBlueMacro')} />
          <p className="mt-3 text-center text-white/40 text-sm">← Arrastra para comparar 2K vs 4K →</p>
        </motion.div>
      </div>
    </section>
  );
};
